import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unseenAnnouncement, setUnseenAnnouncement] = useState(null);
  const initializingRef = useRef(false);

  const initializeAuth = async () => {
    // Prevent multiple simultaneous calls, but allow re-initialization if previous one completed
    if (initializingRef.current) {
      return;
    }
    
    // If user is already set and matches the stored userType, skip reinitialization
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (user && token && user.role === userType) {
      setLoading(false);
      return;
    }
    
    initializingRef.current = true;
    setLoading(true);

    // Add a safety timeout to prevent endless loading
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 10000); // 10-second timeout as fallback

    try {
      if (!token) {
        setLoading(false);
        initializingRef.current = false;
        clearTimeout(safetyTimeout);
        return;
      }

      api.defaults.headers.common['x-auth-token'] = token;
      
      // Check if supplier is on wrong dashboard and redirect
      if (userType === 'supplier' && window.location.pathname === '/dashboard') {
        initializingRef.current = false;
        window.location.href = '/supplier/dashboard';
        return;
      }
      
      try {
        // Get user data - use different endpoint for suppliers
        if (userType === 'supplier') {
          try {
            const supplierRes = await api.get('/api/supplier/user');

            // Set user with supplier data
            setUser(supplierRes.data);
          } catch (error) {
            // Handle rate limiting (429) errors
            if (error.response?.status === 429) {
              initializingRef.current = false; // Reset flag so retry can proceed
              // Wait 5 seconds before retrying
              setTimeout(() => {
                initializeAuth();
              }, 5000);
              return; // Exit early to prevent setting loading to false
            }

            // If we get a 502 Bad Gateway error, create a temporary supplier user
            // so that the UI doesn't break while the backend issue is being fixed
            if (error.response?.status === 502) {
              const tempSupplierUser = {
                firstName: 'Temporary',
                lastName: 'User',
                email: 'temp@example.com',
                role: 'supplier',
                _id: 'temp-id'
              };
              setUser(tempSupplierUser);
            } else if (error.response?.status === 401) {
              // Handle unauthorized errors by logging out
              localStorage.removeItem('token');
              localStorage.removeItem('userType');
            } else if (error.response?.status === 403) {
              localStorage.removeItem('token');
              localStorage.removeItem('userType');
            }
          } finally {
            // Ensure loading is set to false for suppliers
            setLoading(false);
            initializingRef.current = false;
            clearTimeout(safetyTimeout);
          }
        } else if (userType === 'admin') {
          try {
            const userRes = await api.get('/api/auth/me');
            setUser(userRes.data);
          } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
              localStorage.removeItem('token');
              localStorage.removeItem('userType');
            }
          } finally {
            setLoading(false);
            initializingRef.current = false;
            clearTimeout(safetyTimeout);
          }
        } else {
          // For regular users
          try {
            const userRes = await api.get('/api/auth/me');
            setUser(userRes.data);

            // If user is admin, skip wedding fetch
            if (userRes.data.role === 'admin') {
              setUser(userRes.data);
              setLoading(false);
              initializingRef.current = false;
              clearTimeout(safetyTimeout);
              return;
            }

            // Check for pending invites
            try {
              const invitesRes = await api.get('/api/users/pending-invites');
              if (invitesRes.data && invitesRes.data.length > 0) {
                setError(invitesRes.data);
                setLoading(false);
                initializingRef.current = false;
                clearTimeout(safetyTimeout);
                return;
              }
            } catch (invitesError) {}

            // Set wedding data from user response if available
            if (userRes.data.wedding) {
              setWedding(userRes.data.wedding);
              localStorage.setItem('weddingId', userRes.data.wedding._id);
            } else {
              try {
                const invitesRes = await api.get('/api/users/pending-invites');
                if (invitesRes.data && invitesRes.data.length > 0) {
                  // Don't set error here, let the NoWeddingAccess component handle it
                  setError(null);
                } else {
                  // Set a flag to indicate user needs to create wedding
                  localStorage.setItem('needsWedding', 'true');
                }
              } catch (invitesError) {
                // If we can't check invites, assume user needs to create wedding
                localStorage.setItem('needsWedding', 'true');
              }
            }

            setUser(userRes.data);
            setLoading(false);
            initializingRef.current = false;
            clearTimeout(safetyTimeout);
          } catch (error) {
            // If unauthorized or forbidden, clear token and redirect
            if (error.response?.status === 401 || error.response?.status === 403) {
              localStorage.removeItem('token');
              localStorage.removeItem('weddingId');

              // If token is still valid but user data fails, try forcing a redirect to no-access
              // This helps with new registrations
              if (token && error.response?.status === 403) {
                // Set a flag to indicate this is a new user situation
                localStorage.setItem('newUserRedirect', 'true');

                // We'll still set loading to false to let the PrivateRoute handle it
              }
            }

            setLoading(false);
            initializingRef.current = false;
            clearTimeout(safetyTimeout);
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('weddingId');
        }
        setLoading(false);
        initializingRef.current = false;
        clearTimeout(safetyTimeout);
      }
    } catch (error) {
      setLoading(false);
      initializingRef.current = false;
      clearTimeout(safetyTimeout);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadUnseenAnnouncement = async () => {
      if (!user) {
        setUnseenAnnouncement(null);
        return;
      }

      try {
        const response = await api.get('/api/announcements/unseen');
        setUnseenAnnouncement(response.data?.announcement || null);
      } catch (announcementError) {
        setUnseenAnnouncement(null);
      }
    };

    loadUnseenAnnouncement();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [user?._id]);

  // Listen for localStorage changes to re-initialize auth
  useEffect(() => {
    let debounceTimeout;
    
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'userType') {
        // Reset the initialization flag to allow re-initialization
        initializingRef.current = false;
        // Debounce to prevent multiple rapid calls
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          initializeAuth();
        }, 500); // Wait 500ms before calling
      }
    };

    // Also listen for custom events that can be triggered manually
    const handleAuthChange = () => {
      // Reset the initialization flag to allow re-initialization
      initializingRef.current = false;
      // Debounce to prevent multiple rapid calls
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        initializeAuth();
      }, 500); // Wait 500ms before calling
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleAuthChange);
    return () => {
      clearTimeout(debounceTimeout);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, []);



  const login = async (email, password, isSupplier = false, deviceId = null) => {
    try {
      // Use different endpoint for supplier login
      const endpoint = isSupplier ? '/api/supplier/login' : '/api/auth/login';
      const response = await api.post(endpoint, { email, password, deviceId });
      
      if (response.data.requiresVerification) {
        return {
          requiresVerification: true,
          userId: response.data.userId,
          deviceId: response.data.deviceId,
          message: response.data.message,
          emailSent: response.data.emailSent
        };
      }

      if (response.data.requiresEmailVerification) {
        return {
          requiresEmailVerification: true,
          userId: response.data.userId,
          message: response.data.message,
          emailSent: response.data.emailSent !== false // Default to true if not specified
        };
      }

      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      
      // Set userType in localStorage for supplier
      if (isSupplier || response.data.userType === 'supplier') {
        localStorage.setItem('userType', 'supplier');
      }
      
      if (response.data.deviceId) {
        localStorage.setItem('deviceId', response.data.deviceId);
      }

      // Set token in API headers
      api.defaults.headers.common['x-auth-token'] = response.data.token;

      // Set user data directly from login response
      if (response.data.user) {
        setUser(response.data.user);
        setUnseenAnnouncement(response.data.unseenAnnouncement || null);

        // Set wedding data if available in response
        if (response.data.wedding) {
          setWedding(response.data.wedding);
          localStorage.setItem('weddingId', response.data.wedding._id);
        } else if (!isSupplier && response.data.userType !== 'supplier') {
          // For non-supplier users, try to load wedding data if not in response
          try {
            const userRes = await api.get('/api/auth/me');
            if (userRes.data && userRes.data.wedding) {
              setWedding(userRes.data.wedding);
              localStorage.setItem('weddingId', userRes.data.wedding._id);
            }
          } catch (weddingError) {}
        }

        // Set loading to false to indicate auth is initialized
        setLoading(false);
      }
      
      // Redirect suppliers to the correct dashboard if they're on the wrong one
      if (isSupplier || response.data.userType === 'supplier') {
        if (window.location.pathname === '/dashboard') {
          window.location.href = '/supplier/dashboard';
        }
      }
      
      return { 
        success: true,
        user: response.data.user
      };
    } catch (error) {
      // Check for email verification requirement in error response
      if (error.response?.data?.requiresEmailVerification) {
        return {
          requiresEmailVerification: true,
          userId: error.response.data.userId,
          message: error.response.data.message,
          emailSent: error.response.data.emailSent !== false // Default to true if not specified
        };
      }

      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('weddingId');
    localStorage.removeItem('userType');
    localStorage.removeItem('deviceId');
    localStorage.removeItem('supplierRegistrationData');
    localStorage.removeItem('supplierRegistrationStep');
    localStorage.removeItem('supplierRegistrationBusinessId');
    localStorage.removeItem('currentBusinessId');
    delete api.defaults.headers.common['x-auth-token'];
    delete api.defaults.headers.common['x-wedding-id'];
    setUser(null);
    setWedding(null);
    setUnseenAnnouncement(null);
  };

  const dismissAnnouncement = async (announcementId) => {
    if (!announcementId) return;
    try {
      await api.patch('/api/announcements/seen', { announcementId });
      setUnseenAnnouncement((current) => {
        if (!current || current.id !== announcementId) return current;
        return null;
      });
      setUser((current) => {
        if (!current) return current;
        return { ...current, lastSeenAnnouncementId: announcementId };
      });
    } catch (dismissError) {}
  };

  const reinitializeAuth = () => {
    setLoading(true);
    initializeAuth();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      wedding,
      loading,
      error,
      unseenAnnouncement,
      login,
      logout,
      dismissAnnouncement,
      reinitializeAuth,
      setUser,
      setWedding
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 